<template>
  <div v-if="isBadgeStatus" :class="`status-badge status--${status}`">
    <span v-if="$slots.prefix" class="status-badge__prefix mr-4"><slot name="prefix"/></span>
    <span :class="`status-badge__status status-badge__status--${status}`" />
    <span class="status-badge__text" :data-cy="dataCy">{{ statusLabels[status] || '' }}</span>
    <span v-if="$slots.postfix" class="status-badge__postfix ml-4"><slot name="postfix"/></span>
  </div>
  <div v-else :class="`status status--${status}`">
    <span v-if="$slots.prefix" class="status-badge__prefix mr-4"><slot name="prefix"/></span>
    <span :data-cy="dataCy">{{ statusLabels[status] || '' }}</span>
    <span v-if="$slots.postfix" class="status-badge__postfix ml-4"><slot name="postfix"/></span>
  </div>
</template>

<script>
import {
  ORDER_STATUS_PENDING,
  ORDER_STATUS_PARTIALLY_ACCEPTED,
  ORDER_STATUS_ACCEPTED,
  ORDER_STATUS_DECLINED,
  ORDER_STATUS_SHIPPED,
  ORDER_STATUS_NEEDS_APPROVAL,
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_IN_DISPUTE,
  ORDER_STATUS_ABANDONED,

  ACCOUNT_STATUS_ACTIVE,
  ACCOUNT_STATUS_DEACTIVATED,
  ACCOUNT_STATUS_DRAFT,
  ACCOUNT_STATUS_INVITE_SENT,
  statusLabels,
  ORDER_STATUS_COMPLETED_AFTER_DISPUTE,
  QUANTITY_STATUS_OUT_OF_STOCK,
  QUANTITY_STATUS_NOT_TRACKING,
  PRODUCT_VISIBILITY_VISIBLE,
  PRODUCT_VISIBILITY_HIDDEN,
  PENDING_UPDATES_STATUS_ACTIVE,
  PENDING_UPDATES_STATUS_REQUEST_PENDING,
  pendingUpdatesStatusLabels,

  accountStatusLabels,
  TARGET_ACCOUNT,
  PENDING_ACCOUNT_OPENING,
  ACCOUNT_OPEN_YET_TO_BUY,
  NOT_ORDERING_ACCOUNT,
  ORDERING_ACCOUNT,
  NO_SPENDINGS,
  ExpenseType,
  ExpenseTypeLabels,
  ActivityType,
  ActivityTypeLabels,
  CURRENCY_NOT_MATCHING,
  PENDING_INVITE_SENT,
  PRIMARY_CONTACT_LABEL,
  PRIMARY_CONTACT_LABEL_DEFAULT,
  ACCOUNT_TYPE_PREMIUM,
  WAREHOUSE_PRIMARY_DEFAULT,
} from '@/util/constants';

export default {
  props: {
    status: {
      type: String,
      required: true,
      validator: value => [
        ORDER_STATUS_PENDING,
        ORDER_STATUS_PARTIALLY_ACCEPTED,
        ORDER_STATUS_ACCEPTED,
        ORDER_STATUS_DECLINED,
        ORDER_STATUS_SHIPPED,
        ORDER_STATUS_NEEDS_APPROVAL,
        ORDER_STATUS_COMPLETED,
        ORDER_STATUS_IN_DISPUTE,
        ORDER_STATUS_COMPLETED_AFTER_DISPUTE,
        ORDER_STATUS_ABANDONED,

        ACCOUNT_STATUS_ACTIVE,
        ACCOUNT_STATUS_DEACTIVATED,
        ACCOUNT_STATUS_DRAFT,
        ACCOUNT_STATUS_INVITE_SENT,

        QUANTITY_STATUS_OUT_OF_STOCK,
        QUANTITY_STATUS_NOT_TRACKING,

        PRODUCT_VISIBILITY_VISIBLE,
        PRODUCT_VISIBILITY_HIDDEN,

        PENDING_UPDATES_STATUS_ACTIVE,
        PENDING_UPDATES_STATUS_REQUEST_PENDING,
        PENDING_INVITE_SENT,

        ...Object.values(ExpenseType),
        ...Object.values(ActivityType),

        TARGET_ACCOUNT,
        PENDING_ACCOUNT_OPENING,
        ACCOUNT_OPEN_YET_TO_BUY,
        NOT_ORDERING_ACCOUNT,
        ORDERING_ACCOUNT,
        NO_SPENDINGS,

        CURRENCY_NOT_MATCHING,

        PRIMARY_CONTACT_LABEL,
        PRIMARY_CONTACT_LABEL_DEFAULT,

        ACCOUNT_TYPE_PREMIUM,
        WAREHOUSE_PRIMARY_DEFAULT,
      ].includes(value),
    },
    dataCy: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    statusLabels() {
      return {
        ...statusLabels,
        [ACCOUNT_STATUS_ACTIVE]: 'Active',
        [ACCOUNT_STATUS_DEACTIVATED]: 'Deactivated',
        [ACCOUNT_STATUS_DRAFT]: 'Draft',
        [ACCOUNT_STATUS_INVITE_SENT]: 'Invite sent',
        [QUANTITY_STATUS_OUT_OF_STOCK]: 'Out of stock',
        [QUANTITY_STATUS_NOT_TRACKING]: 'Not tracking',
        [PRODUCT_VISIBILITY_VISIBLE]: 'Visible',
        [PRODUCT_VISIBILITY_HIDDEN]: 'Hidden',
        [CURRENCY_NOT_MATCHING]: 'Currency not matching',
        ...ExpenseTypeLabels,
        ...ActivityTypeLabels,
        ...pendingUpdatesStatusLabels,
        ...accountStatusLabels,
        [PRIMARY_CONTACT_LABEL]: 'Primary',
        [PRIMARY_CONTACT_LABEL_DEFAULT]: 'Default Primary',
        [ACCOUNT_TYPE_PREMIUM]: 'Premium',
        [WAREHOUSE_PRIMARY_DEFAULT]: 'Default',
      };
    },
    isBadgeStatus() {
      return [
        ORDER_STATUS_PARTIALLY_ACCEPTED,
        ORDER_STATUS_ACCEPTED,
        ORDER_STATUS_DECLINED,
        ORDER_STATUS_SHIPPED,
        ORDER_STATUS_COMPLETED_AFTER_DISPUTE,
        ORDER_STATUS_ABANDONED,

        ACCOUNT_STATUS_INVITE_SENT,

        PENDING_UPDATES_STATUS_REQUEST_PENDING,
      ].includes(this.status);
    },
  },
};
</script>

<style scoped lang="scss">
  .status-badge {
    display: inline-flex;
    align-items: center;
    background-color: rgba(108, 121, 149, .12);
    color: $color-gray-6C;
    padding: 0.125rem 0.375rem;
    border-radius: 0.125rem;
    max-width: 100%;

    &__text {
      @include font-size(11px);
      font-weight: 600;
      text-transform: uppercase;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__status {
      display: inline-block;
      border-radius: 50%;
      margin-right: .25rem;
      flex-shrink: 0;
      width: .25rem;
      height: .25rem;
      background-color: $color-gray-6C;
      &--accepted {
        background-color: $color-primary-green;
      }
      &--partially_accepted,
      &--invite_sent,
      &--connection_pending {
        background-color: $color-primary-blue;
      }
      &--completed_after_dispute,
      &--declined {
        background-color: $color-primary-red;
      }
      &--out_for_delivery,
      &--shipped {
        background-color: $color-primary-orange;
      }
    }
  }
  .status {
    @include font-size(11px);
    font-weight: 600;
    text-transform: uppercase;
    display: inline-block;
    padding: 0.125rem 0.375rem;
    border-radius: 0.125rem;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;

    &--invitation_sent,
    &--currency-no-matching {
      border: 1px solid $color-gray-E1;
      background-color: $color-white;
      color: $color-gray-6C;
    }

    &--default,
    &--primary,
    &--todo,
    &--business_development {
      background-color: $color-pastel-blue;
      color: $color-primary-blue;
    }

    &--travel,
    &--premium,
    &--pending {
      background-color: rgba($color-primary-orange, .16);
      color: $color-primary-orange;
    }

    &--meeting {
      background-color: rgba($color-primary-carrot, .16);
      color: $color-primary-carrot;
    }

    &--check_in,
    &--needs_approval {
      background-color: rgba($color-primary-purple, .16);
      color: $color-primary-purple;
    }

    &--completed_after_dispute,
    &--completed,
    &--active,
    &--visible,
    &--active,
    &--call {
      background-color: rgba($color-primary-green, .16);
      color: $color-primary-green;
    }
    &--in_dispute,
    &--deactivated,
    &--out_of_stock {
      background-color: rgba($color-primary-red, .16);
      color: $color-primary-red;
    }

    &--other,
    &--target_account,
    &--pending_account_opening,
    &--account_open,
    &--not_ordering_account,
    &--ordering_account,
    &--no_spendings,
    &--draft,
    &--not_tracking,
    &--hidden,
    &--primary_default {
      background-color: rgba($color-gray-6C, .16);
      color: #6C7995;
    }
  }
</style>
